import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../config/api';
import { DayPlanCard } from './DayPlanCard';
import { SwapMealModal } from './SwapMealModal';
import {
  ArrowPathIcon,
  ShoppingCartIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

interface PlanCalendarViewProps {
  planId: number;
  onClose?: () => void;
  coachMode?: boolean;
  clientId?: number;
}

export const PlanCalendarView: React.FC<PlanCalendarViewProps> = ({ planId, onClose, coachMode, clientId }) => {
  const [selectedEntry, setSelectedEntry] = useState<any>(null);
  const [showSwapModal, setShowSwapModal] = useState(false);

  const queryClient = useQueryClient();

  // Fetch plan details
  const { data: planData, isLoading, error } = useQuery({
    queryKey: coachMode ? ['meal-plan', planId, 'client', clientId] : ['meal-plan', planId],
    queryFn: () => apiClient.getMealPlan(planId, coachMode ? clientId : undefined),
    enabled: !!planId,
  });

  // Regenerate day mutation
  const regenerateDayMutation = useMutation({
    mutationFn: (dayNumber: number) => apiClient.regenerateMealPlanDay(planId, dayNumber, coachMode ? clientId : undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: coachMode ? ['meal-plan', planId, 'client', clientId] : ['meal-plan', planId] });
    },
  });

  // Activate plan mutation
  const activateMutation = useMutation({
    mutationFn: () => apiClient.activateMealPlan(planId, coachMode ? clientId : undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: coachMode ? ['meal-plan', planId, 'client', clientId] : ['meal-plan', planId] });
      queryClient.invalidateQueries({ queryKey: coachMode ? ['meal-plans', 'client', clientId] : ['meal-plans'] });
    },
  });

  // Toggle lock mutation
  const toggleLockMutation = useMutation({
    mutationFn: (entryId: number) => apiClient.toggleMealLock(planId, entryId, coachMode ? clientId : undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: coachMode ? ['meal-plan', planId, 'client', clientId] : ['meal-plan', planId] });
    },
  });

  const handleSwapMeal = (entry: any) => {
    setSelectedEntry(entry);
    setShowSwapModal(true);
  };

  const handleLockMeal = (entryId: number) => {
    toggleLockMutation.mutate(entryId);
  };

  const handleRegenerateDay = (dayNumber: number) => {
    if (window.confirm('This will regenerate all unlocked meals for this day. Continue?')) {
      regenerateDayMutation.mutate(dayNumber);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="bg-gray-200 rounded-lg h-24 animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[...Array(7)].map((_, i) => (
            <div key={i} className="bg-gray-200 rounded-lg h-64 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (error || !planData?.plan) {
    return (
      <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
        <XMarkIcon className="h-12 w-12 mx-auto text-red-400" />
        <h3 className="mt-4 text-lg font-medium text-gray-900">Failed to load meal plan</h3>
        <p className="mt-2 text-gray-500">Please try again later.</p>
      </div>
    );
  }

  const plan = planData.plan;
  const days = plan.days || [];

  // Calculate total week nutrition
  const weekNutrition = days.reduce(
    (acc: any, day: any) => {
      const dayNutrition = day.daily_nutrition || {};
      return {
        calories: acc.calories + (dayNutrition.calories || 0),
        protein: acc.protein + (dayNutrition.protein || 0),
        carbs: acc.carbs + (dayNutrition.carbs || 0),
        fat: acc.fat + (dayNutrition.fat || 0),
      };
    },
    { calories: 0, protein: 0, carbs: 0, fat: 0 }
  );

  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  return (
    <div className="space-y-6">
      {/* Plan Header */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {plan.name || `Meal Plan: ${plan.start_date} to ${plan.end_date}`}
            </h2>
            <div className="mt-2 flex items-center gap-4 text-sm text-gray-500">
              <span>
                {new Date(plan.start_date).toLocaleDateString('en-GB', {
                  weekday: 'short',
                  day: 'numeric',
                  month: 'short'
                })} - {new Date(plan.end_date).toLocaleDateString('en-GB', {
                  weekday: 'short',
                  day: 'numeric',
                  month: 'short'
                })}
              </span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                plan.status === 'active' ? 'bg-green-100 text-green-800' :
                plan.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {plan.status}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {plan.status === 'draft' && (
              <button
                onClick={() => activateMutation.mutate()}
                className="flex items-center gap-1 px-3 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700"
              >
                <CheckCircleIcon className="h-4 w-4" />
                Activate Plan
              </button>
            )}
            <button
              onClick={() => window.location.href = `#shopping?plan=${planId}`}
              className="flex items-center gap-1 px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
            >
              <ShoppingCartIcon className="h-4 w-4" />
              Shopping List
            </button>
          </div>
        </div>

        {/* Weekly Nutrition Summary */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-blue-700">
              {Math.round(weekNutrition.calories / 7)}
            </div>
            <div className="text-xs text-blue-600">Avg Cal/Day</div>
          </div>
          <div className="bg-green-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-green-700">
              {Math.round(weekNutrition.protein / 7)}g
            </div>
            <div className="text-xs text-green-600">Avg Protein/Day</div>
          </div>
          <div className="bg-amber-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-amber-700">
              {Math.round(weekNutrition.carbs / 7)}g
            </div>
            <div className="text-xs text-amber-600">Avg Carbs/Day</div>
          </div>
          <div className="bg-rose-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-rose-700">
              {Math.round(weekNutrition.fat / 7)}g
            </div>
            <div className="text-xs text-rose-600">Avg Fat/Day</div>
          </div>
        </div>
      </div>

      {/* Days Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {days.map((day: any, index: number) => (
          <DayPlanCard
            key={day.id || index}
            day={day}
            dayName={dayNames[index] || `Day ${index + 1}`}
            planId={planId}
            onSwapMeal={handleSwapMeal}
            onLockMeal={handleLockMeal}
            onRegenerateDay={() => handleRegenerateDay(day.day_number)}
            isRegenerating={regenerateDayMutation.isPending}
          />
        ))}
      </div>

      {/* Therapeutic Insights Panel */}
      {plan.generation_params?.intelligence?.neo4j?.available && (
        <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-lg border border-teal-200 p-6">
          <h3 className="font-semibold text-teal-900 flex items-center gap-2">
            <DocumentTextIcon className="h-5 w-5" />
            Therapeutic Insights
          </h3>
          <div className="mt-3 text-sm text-teal-800 space-y-2">
            {plan.generation_params.intelligence.neo4j.foodsForDeficiencies?.map((item: any, i: number) => (
              <p key={i}>
                <strong>{item.nutrient}:</strong> Focus on {item.foods.slice(0, 3).map((f: any) => f.name).join(', ')}
              </p>
            ))}
            {plan.generation_params.intelligence.neo4j.nutrientSynergies?.slice(0, 3).map((synergy: any, i: number) => (
              <p key={i} className="text-teal-600">
                Tip: Combine {synergy.nutrient1} with {synergy.nutrient2} for better absorption
              </p>
            ))}
          </div>
        </div>
      )}

      {/* Swap Modal */}
      {showSwapModal && selectedEntry && (
        <SwapMealModal
          planId={planId}
          entry={selectedEntry}
          onClose={() => {
            setShowSwapModal(false);
            setSelectedEntry(null);
          }}
          onSwapComplete={() => {
            queryClient.invalidateQueries({ queryKey: coachMode ? ['meal-plan', planId, 'client', clientId] : ['meal-plan', planId] });
            setShowSwapModal(false);
            setSelectedEntry(null);
          }}
          coachMode={coachMode}
          clientId={clientId}
        />
      )}
    </div>
  );
};
